import { useMutation, UseMutationResult, useQuery, useQueryClient, QueryResult } from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type {
    AccountId,
    AuthorizedCustomerRequest,
    ApprovalResult,
    UserId,
    AuthorizedCustomer,
} from '../generated-types';
import {
    addAuthorizedCustomer,
    deleteAuthorizedCustomer,
    getAuthorizedCustomers,
    validateCustomerName,
} from '../services/accountService';
import { ErrorTypes } from '../utils/gtm/errorTypes';
import { pushErrorToDataLayer } from '../utils/gtm/gtmService';

export const useAuthorizedCustomers = (accountId?: AccountId, options = {}): QueryResult<AuthorizedCustomer[]> =>
    useQuery<AuthorizedCustomer[]>({
        queryKey: ['authorizedCustomers', accountId?.value],
        queryFn: () => getAuthorizedCustomers(accountId!),
        enabled: !!accountId,
        staleTime: Infinity,
        ...options,
    });

export const useValidateName = (
    accountId: AccountId,
    customerId: UserId,
    customerName: string,
    onError: (error: RequestExceptionWithResponse) => void,
    onSuccess: (valid: boolean) => void,
): UseMutationResult<boolean, RequestExceptionWithResponse, void> =>
    useMutation<boolean, RequestExceptionWithResponse>({
        mutationKey: ['validateName'],
        mutationFn: () => validateCustomerName(accountId, { customerNumber: customerId, name: customerName }),
        onSuccess,
        onError,
    });

export const useUpdateAuthorizedCustomers = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: ['authorizedCustomers'] });
};

export const useAddAuthorizedCustomer = (
    accountId?: AccountId,
    onSuccess?: (status: ApprovalResult) => void,
    onError?: () => void,
) =>
    useMutation<ApprovalResult, RequestExceptionWithResponse, AuthorizedCustomerRequest>({
        mutationKey: ['authcust'],
        mutationFn: (authorizedCustomerRequest: AuthorizedCustomerRequest) =>
            addAuthorizedCustomer(accountId!, authorizedCustomerRequest),
        onSuccess,
        onError: (error: Error) => {
            pushErrorToDataLayer(error, ErrorTypes.ADD_AUTHORIZED_CUSTOMER);
            onError?.();
        },
    });

export const useDeleteAuthorizedCustomer = (accountId?: AccountId) => {
    const refreshAuthorizedCustomers = useUpdateAuthorizedCustomers();
    return useMutation<void, RequestExceptionWithResponse, UserId>({
        mutationKey: ['delete-authorized-customer'],
        mutationFn: (customerId: UserId) => deleteAuthorizedCustomer(accountId!, customerId),
        onSuccess: () => refreshAuthorizedCustomers(),
        onError: (error: Error) => pushErrorToDataLayer(error, ErrorTypes.REMOVE_AUTHORIZED_CUSTOMER),
    });
};
